/**
 * Projects API
 *
 * CRUD operations for projects - groups of content with shared default context.
 */

import { supabase } from '@/integrations/supabase/client';
import type { Project, ContentContext } from '@/types/context';
import { fromProjectRow } from '@/types/context';

// ============================================================================
// Types
// ============================================================================

export interface CreateProjectRequest {
  name: string;
  description?: string;
  defaultContext?: Partial<ContentContext>;
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string;
  defaultContext?: Partial<ContentContext>;
}

export interface DeleteProjectOptions {
  /** Delete all content pieces currently assigned to the project before deleting the project itself */
  deleteContent?: boolean;
}

// ============================================================================
// CRUD Operations
// ============================================================================

/**
 * Get all projects for the current user.
 */
export async function getProjects(): Promise<Project[]> {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Failed to fetch projects:', error);
    throw new Error(error.message);
  }

  return (data || []).map(fromProjectRow);
}

/**
 * Get a single project by ID.
 */
export async function getProject(id: string): Promise<Project | null> {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    console.error('Failed to fetch project:', error);
    throw new Error(error.message);
  }

  return fromProjectRow(data);
}

/**
 * Create a new project.
 */
export async function createProject(request: CreateProjectRequest): Promise<Project> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const ctx = request.defaultContext || {};

  const { data, error } = await supabase
    .from('projects')
    .insert({
      user_id: user.id,
      name: request.name,
      description: request.description || null,
      default_writing_style_id: ctx.writingStyleId || null,
      default_brand_profile_id: ctx.brandProfileId || null,
      default_purpose: ctx.purpose || null,
      default_channel: ctx.channel || null,
      default_target_audience: ctx.targetAudience || null,
      default_seo_keywords: ctx.seoKeywords || null,
      default_story_framework: ctx.storyFramework || null,
    })
    .select()
    .single();

  if (error) {
    console.error('Failed to create project:', error);
    throw new Error(error.message);
  }

  return fromProjectRow(data);
}

/**
 * Update an existing project.
 */
export async function updateProject(id: string, request: UpdateProjectRequest): Promise<Project> {
  const updates: Record<string, unknown> = {};

  if (request.name !== undefined) updates.name = request.name;
  if (request.description !== undefined) updates.description = request.description;

  if (request.defaultContext) {
    const ctx = request.defaultContext;
    if (ctx.writingStyleId !== undefined) updates.default_writing_style_id = ctx.writingStyleId;
    if (ctx.brandProfileId !== undefined) updates.default_brand_profile_id = ctx.brandProfileId;
    if (ctx.purpose !== undefined) updates.default_purpose = ctx.purpose;
    if (ctx.channel !== undefined) updates.default_channel = ctx.channel;
    if (ctx.targetAudience !== undefined) updates.default_target_audience = ctx.targetAudience;
    if (ctx.seoKeywords !== undefined) updates.default_seo_keywords = ctx.seoKeywords;
    if (ctx.storyFramework !== undefined) updates.default_story_framework = ctx.storyFramework;
  }

  const { data, error } = await supabase
    .from('projects')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Failed to update project:', error);
    throw new Error(error.message);
  }

  return fromProjectRow(data);
}

/**
 * Delete a project.
 * Content pieces in the project will have their project_id set to null.
 */
export async function deleteProject(id: string, options: DeleteProjectOptions = {}): Promise<void> {
  if (options.deleteContent) {
    const { error: contentError } = await supabase
      .from('content_pieces')
      .delete()
      .eq('project_id', id);

    if (contentError) {
      console.error('Failed to delete project content:', contentError);
      throw new Error(contentError.message);
    }
  }

  const { error } = await supabase
    .from('projects')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Failed to delete project:', error);
    throw new Error(error.message);
  }
}

// ============================================================================
// Project Content Operations
// ============================================================================

/**
 * Get all content pieces in a project.
 */
export async function getProjectContent(projectId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('content_pieces')
    .select('id')
    .eq('project_id', projectId)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Failed to fetch project content:', error);
    throw new Error(error.message);
  }

  return (data || []).map(d => d.id);
}

/**
 * Add a content piece to a project.
 */
export async function addContentToProject(contentId: string, projectId: string): Promise<void> {
  const { error } = await supabase
    .from('content_pieces')
    .update({ project_id: projectId })
    .eq('id', contentId);

  if (error) {
    console.error('Failed to add content to project:', error);
    throw new Error(error.message);
  }
}

/**
 * Remove a content piece from a project (sets project_id to null).
 */
export async function removeContentFromProject(contentId: string): Promise<void> {
  const { error } = await supabase
    .from('content_pieces')
    .update({ project_id: null })
    .eq('id', contentId);

  if (error) {
    console.error('Failed to remove content from project:', error);
    throw new Error(error.message);
  }
}

/**
 * Move multiple content pieces to a project.
 */
export async function moveContentToProject(contentIds: string[], projectId: string | null): Promise<void> {
  const { error } = await supabase
    .from('content_pieces')
    .update({ project_id: projectId })
    .in('id', contentIds);

  if (error) {
    console.error('Failed to move content to project:', error);
    throw new Error(error.message);
  }
}

// ============================================================================
// Context Resolution
// ============================================================================

/**
 * Get the effective context for a content piece (project defaults + document overrides).
 * Uses the database function for consistent resolution.
 */
export async function getEffectiveContext(contentId: string): Promise<ContentContext | null> {
  const { data, error } = await supabase
    .rpc('get_effective_context', { p_content_piece_id: contentId });

  if (error) {
    console.error('Failed to get effective context:', error);
    throw new Error(error.message);
  }

  if (!data || data.length === 0) return null;

  const row = data[0];
  return {
    writingStyleId: row.writing_style_id,
    brandProfileId: row.brand_profile_id,
    purpose: row.purpose,
    channel: row.channel,
    targetAudience: row.target_audience,
    seoKeywords: row.seo_keywords,
    storyFramework: row.story_framework,
  };
}

/**
 * Get project default context.
 */
export async function getProjectDefaultContext(projectId: string): Promise<ContentContext | null> {
  const project = await getProject(projectId);
  if (!project) return null;

  return {
    writingStyleId: project.defaultWritingStyleId,
    brandProfileId: project.defaultBrandProfileId,
    purpose: project.defaultPurpose,
    channel: project.defaultChannel,
    targetAudience: project.defaultTargetAudience,
    seoKeywords: project.defaultSeoKeywords,
    storyFramework: project.defaultStoryFramework,
  };
}
